import type { MermaidConfig } from 'mermaid'
import { currentSauce, isDarkSauce, paletteToken } from './sauces'

/**
 * Mermaid diagrams inside rendered markdown. Diagram source is untrusted, so
 * this keeps the same shape as markdown.ts — two layers that don't depend on
 * each other. Mermaid renders locked down, and DOMPurify sanitises the SVG it
 * hands back.
 *
 * Mermaid is by far the heaviest dependency here, so it is only imported once a
 * paste actually contains a diagram. Plain markdown never pays for it.
 */

/** markdown-it turns ```mermaid into this, and `class` survives the sanitiser. */
const SELECTOR = 'pre > code.language-mermaid'

/**
 * Config keys a diagram may not set through an `%%{init}%%` directive.
 *
 * Mermaid's own default list covers the first six. The theme keys are added
 * because `themeCSS` is arbitrary CSS injection, and pinning them is what makes
 * the `<style>` block mermaid embeds in its output safe to keep: every rule in
 * it then comes from the config below rather than from the paste.
 */
const PINNED_KEYS = [
  'secure',
  'securityLevel',
  'startOnLoad',
  'maxTextSize',
  'maxEdges',
  'suppressErrorRendering',
  'theme',
  'themeCSS',
  'themeVariables',
  'fontFamily',
  'altFontFamily',
]

/**
 * `foreignObject` is the one that matters: it is how mermaid smuggles HTML into
 * an SVG for rich labels, and dropping it means even a diagram that talks its
 * way past `htmlLabels: false` cannot get markup into the page. `image` goes too
 * so a diagram can't beacon a viewer's IP to a remote host.
 */
const SANITIZE = {
  USE_PROFILES: { svg: true, svgFilters: true },
  ADD_TAGS: ['style'],
  ADD_ATTR: ['style'],
  FORBID_TAGS: ['script', 'foreignObject', 'iframe', 'image'],
  ALLOW_DATA_ATTR: false,
}

interface Renderer {
  render: (id: string, source: string) => Promise<{ svg: string }>
  initialize: (config: MermaidConfig) => void
  sanitize: (svg: string) => string
}

let loading: Promise<Renderer> | null = null
let counter = 0

function config(): MermaidConfig {
  const dark = isDarkSauce(currentSauce())
  return {
    startOnLoad: false,
    // 'strict' sanitises label text and refuses the click/callback directives
    // that would otherwise let a diagram run script.
    securityLevel: 'strict',
    secure: PINNED_KEYS,
    // We report failures ourselves and leave the source visible; mermaid's own
    // error diagram would replace the block with a bomb graphic instead.
    suppressErrorRendering: true,
    maxTextSize: 50_000,
    maxEdges: 500,
    // SVG <text> labels rather than foreignObject HTML.
    htmlLabels: false,
    flowchart: { htmlLabels: false, useMaxWidth: true },
    theme: 'base',
    fontFamily: 'inherit',
    themeVariables: {
      darkMode: dark,
      background: paletteToken('--surface'),
      primaryColor: paletteToken('--surface-alt'),
      primaryTextColor: paletteToken('--fg'),
      primaryBorderColor: paletteToken('--border'),
      secondaryColor: paletteToken('--surface'),
      secondaryTextColor: paletteToken('--fg'),
      secondaryBorderColor: paletteToken('--border'),
      tertiaryColor: paletteToken('--bg'),
      tertiaryTextColor: paletteToken('--fg'),
      tertiaryBorderColor: paletteToken('--border'),
      lineColor: paletteToken('--muted'),
      textColor: paletteToken('--fg'),
      mainBkg: paletteToken('--surface-alt'),
      nodeBorder: paletteToken('--border'),
      nodeTextColor: paletteToken('--fg'),
      titleColor: paletteToken('--fg'),
      edgeLabelBackground: paletteToken('--surface'),
      clusterBkg: paletteToken('--bg'),
      clusterBorder: paletteToken('--border'),
      fontSize: '14px',
      errorBkgColor: paletteToken('--surface'),
      errorTextColor: paletteToken('--danger'),
    },
  }
}

function load(): Promise<Renderer> {
  loading ??= (async () => {
    const [{ default: mermaid }, { default: DOMPurify }] = await Promise.all([
      import('mermaid'),
      import('dompurify'),
    ])

    return {
      render: (id, source) => mermaid.render(id, source),
      initialize: (next) => mermaid.initialize(next),
      // The module-level A-tag hook markdown.ts installs also fires here, since
      // both share one DOMPurify instance. Harmless: an <a> in a diagram picks
      // up the same target/rel treatment as one in prose.
      sanitize: (svg) => DOMPurify.sanitize(svg, SANITIZE),
    }
  })()
  return loading
}

/**
 * Replaces every ```mermaid block in `container` with its rendered diagram.
 * Resolves once each block has been either replaced or left alone — a diagram
 * that won't parse keeps its fenced source on screen rather than blanking it.
 *
 * Safe to call again after a sauce change: re-running it against freshly
 * re-inserted markdown redraws the diagrams in the new palette.
 */
export async function renderDiagrams(container: HTMLElement): Promise<void> {
  const blocks = [...container.querySelectorAll<HTMLElement>(SELECTOR)]
  if (!blocks.length) return

  const renderer = await load()
  // Re-initialised per pass so a sauce change is picked up on redraw.
  renderer.initialize(config())

  for (const code of blocks) {
    const block = code.parentElement
    if (!block) continue

    const source = code.textContent ?? ''
    if (!source.trim()) continue

    const id = `mermaid-${(counter += 1)}`
    try {
      const { svg } = await renderer.render(id, source)
      const figure = document.createElement('figure')
      figure.className = 'diagram'
      figure.innerHTML = renderer.sanitize(svg)
      block.replaceWith(figure)
    } catch {
      // Keep the fence: a typo in one diagram shouldn't cost the whole page.
      block.classList.add('diagram-error')
    } finally {
      // Mermaid measures in a detached probe node it usually cleans up itself.
      // Belt and braces, because a leaked one would sit in the document.
      document.getElementById(`d${id}`)?.remove()
    }
  }
}
